import { useReduxState } from 'hooks';
import { FormGroup } from 'components/common/form';
import { CheckboxInput, SelectInput } from 'components/common/form/common';

interface Props {
  blockId: number;
  correctOptionsCount: number;
  noPointReductionForWrongSelections?: boolean;
  onNoPointReductionForWrongSelectionsChange: (value: boolean) => void;
  onRequiredNumberOfCorrectAnswersChange: (value: number) => void;
  requiredNumberOfCorrectAnswers?: number;
}

const EditorBlockChoiceTasksScoringSettings = ({
  blockId,
  correctOptionsCount,
  noPointReductionForWrongSelections = false,
  onNoPointReductionForWrongSelectionsChange,
  onRequiredNumberOfCorrectAnswersChange,
  requiredNumberOfCorrectAnswers
}: Props) => {
  const { messages } = useReduxState();

  const scoringOptions =
    correctOptionsCount > 0
      ? Array.from({ length: correctOptionsCount }, (_, index) => index + 1)
      : [0];

  const selectedNumberOfCorrectAnswers =
    requiredNumberOfCorrectAnswers && requiredNumberOfCorrectAnswers > 0
      ? Math.min(requiredNumberOfCorrectAnswers, correctOptionsCount || 1)
      : correctOptionsCount;

  const scoringSelectOptions = scoringOptions.map((optionValue) => ({
    label:
      optionValue === correctOptionsCount && correctOptionsCount > 0
        ? messages.commonAll
        : `${optionValue}`,
    value: optionValue
  }));

  const handleNoPointReductionForWrongSelectionsChange = (ev) => {
    onNoPointReductionForWrongSelectionsChange(Boolean(ev.target.checked));
  };

  return (
    <FormGroup
      label={messages.editorBlockMultipleChoiceScoring}
      maxWidth="100%"
    >
      <div className="scoring-settings-row">
        <div className="select-wrapper">
          <SelectInput
            id={`multiple-choice-${blockId}-required-correct-answers`}
            isDisabled={correctOptionsCount === 0}
            isSearchable={false}
            onChange={(option: { value: number }) =>
              onRequiredNumberOfCorrectAnswersChange(option?.value || 0)
            }
            options={scoringSelectOptions}
            value={
              scoringSelectOptions.find(
                (option) => option.value === selectedNumberOfCorrectAnswers
              ) || scoringSelectOptions[0]
            }
          />
        </div>
        <span className="label">
          {messages.editorBlockMultipleChoiceNumberCorrectAnswersRequired}
        </span>
      </div>

      <div className="scoring-settings-checkbox-row">
        <CheckboxInput
          checked={noPointReductionForWrongSelections}
          id={`no-point-reduction-for-wrong-selections-${blockId}`}
          label={
            messages.editorBlockMultipleChoiceNoPointReductionForWrongSelections
          }
          onChange={handleNoPointReductionForWrongSelectionsChange}
          stopPropagation={true}
        />
      </div>

      <style jsx>{`
        .scoring-settings-row {
          align-items: center;
          display: flex;
          gap: 10px;
        }
        .scoring-settings-checkbox-row {
          margin-top: 10px;
        }
        .select-wrapper {
          min-width: 80px;
        }
        .select-wrapper :global(.react-select__control) {
          min-height: auto;
        }
        .select-wrapper :global(.react-select__dropdown-indicator) {
          padding: 4px 6px;
        }
      `}</style>
    </FormGroup>
  );
};

export default EditorBlockChoiceTasksScoringSettings;
